'use client';

interface VerifiedBadgeProps {
	/** Judgement level: Reasonable or KnownGood */
	judgement: 'Reasonable' | 'KnownGood';
	/** Size of the badge (default: 14) */
	size?: number;
	/** Additional CSS class names */
	className?: string;
}

/**
 * Verified badge component showing on-chain identity verification status
 *
 * Follows Polkadot ecosystem standards (polkadot-js/apps):
 * - Green checkmark = Reasonable judgement (standard verification)
 * - Gold/Yellow checkmark = KnownGood judgement (highest level, formal KYC)
 *
 * @see https://github.com/polkadot-js/apps/blob/master/packages/react-components/src/AccountName.tsx
 */
function VerifiedBadge({ judgement, size = 14, className = '' }: VerifiedBadgeProps) {
	const isKnownGood = judgement === 'KnownGood';

	// Ecosystem standard: green for Reasonable, gold for KnownGood
	const colorClass = isKnownGood ? 'text-yellow-400' : 'text-green-400';

	return (
		<span
			className={`inline-flex items-center justify-center ${className}`}
			aria-label={isKnownGood ? 'KnownGood verification' : 'Reasonable verification'}
		>
			{/* Simple checkmark icon - ecosystem standard */}
			<svg
				width={size}
				height={size}
				viewBox='0 0 16 16'
				fill='none'
				xmlns='http://www.w3.org/2000/svg'
				className={colorClass}
			>
				{/* Filled circle background */}
				<circle
					cx='8'
					cy='8'
					r='7'
					fill='currentColor'
				/>
				{/* White checkmark */}
				<path
					d='M5 8L7 10L11 6'
					stroke='white'
					strokeWidth='1.5'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
			</svg>
		</span>
	);
}

export default VerifiedBadge;
export type { VerifiedBadgeProps };
